import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Shield, FileText, Users, Lock, Database } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import TrustBar from "../components/TrustBar";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import CookieConsent from "../components/CookieConsent";
import { SITE } from "../lib/site";

const UPDATED = "23 de julho de 2025";

const sections = [
  {
    id: "coleta",
    icon: Database,
    title: "1. Coleta de Dados",
    body: (
      <>
        <p>
          A ASHIR GROUP coleta dados pessoais básicos fornecidos voluntariamente
          pelo usuário por meio de formulários de contato, WhatsApp, e-mail ou
          telefone, incluindo:
        </p>
        <ul className="mt-4 space-y-2 list-disc pl-5 marker:text-[#D4AF37]">
          <li>Nome completo</li>
          <li>E-mail</li>
          <li>Telefone / WhatsApp</li>
          <li>
            Informações complementares que o usuário optar por compartilhar
            (empresa, valor de interesse, tipo de bem etc.)
          </li>
        </ul>
        <p className="mt-4">
          Também podem ser coletados dados de navegação de forma anônima
          (cookies, endereço IP, tipo de navegador), conforme consentimento
          expresso no banner de cookies.
        </p>
      </>
    ),
  },
  {
    id: "finalidade",
    icon: FileText,
    title: "2. Finalidade do Uso",
    body: (
      <>
        <p>Os dados coletados são utilizados exclusivamente para:</p>
        <ul className="mt-4 space-y-2 list-disc pl-5 marker:text-[#D4AF37]">
          <li>
            Atendimento consultivo sobre cartas de consórcio contempladas,
            capital de giro e demais soluções financeiras estruturadas
          </li>
          <li>Elaboração de propostas comerciais personalizadas</li>
          <li>Comunicação sobre o andamento das negociações</li>
          <li>Envio de informativos e oportunidades — somente com consentimento</li>
          <li>
            Cumprimento de obrigações legais, contratuais e regulatórias
            (BACEN, Lei 11.795/08)
          </li>
        </ul>
        <p className="mt-4">
          <strong className="text-white">A ASHIR GROUP não comercializa,
          aluga ou compartilha dados pessoais com terceiros</strong> para fins
          publicitários. Compartilhamentos ocorrem apenas com administradoras
          de consórcio homologadas e órgãos oficiais, quando estritamente
          necessário à operação contratada.
        </p>
      </>
    ),
  },
  {
    id: "direitos",
    icon: Users,
    title: "3. Direitos do Usuário",
    body: (
      <>
        <p>
          Em conformidade com a Lei Geral de Proteção de Dados (Lei nº
          13.709/2018 — LGPD), o titular dos dados pode, a qualquer momento,
          solicitar:
        </p>
        <ul className="mt-4 space-y-2 list-disc pl-5 marker:text-[#D4AF37]">
          <li>Confirmação da existência de tratamento de seus dados</li>
          <li>Acesso aos dados armazenados</li>
          <li>Correção de dados incompletos, inexatos ou desatualizados</li>
          <li>Anonimização, bloqueio ou eliminação de dados desnecessários</li>
          <li>Portabilidade dos dados</li>
          <li>
            Revogação do consentimento e exclusão dos dados pessoais
            tratados com base no consentimento
          </li>
        </ul>
        <p className="mt-4">
          Para exercer qualquer desses direitos, envie e-mail para{" "}
          <a
            href={`mailto:${SITE.email}`}
            className="text-[#D4AF37] underline underline-offset-4 hover:text-[#F0CC59]"
          >
            {SITE.email}
          </a>{" "}
          com o assunto <em>“LGPD — Solicitação do Titular”</em>. Retornaremos
          em até 15 dias úteis.
        </p>
      </>
    ),
  },
  {
    id: "seguranca",
    icon: Lock,
    title: "4. Segurança das Informações",
    body: (
      <>
        <p>
          A ASHIR GROUP adota medidas técnicas e administrativas para proteger
          os dados pessoais contra acessos não autorizados, perda, alteração ou
          destruição indevida, incluindo:
        </p>
        <ul className="mt-4 space-y-2 list-disc pl-5 marker:text-[#D4AF37]">
          <li>Criptografia em trânsito (HTTPS/TLS) em todo o site</li>
          <li>Controle de acesso baseado em funções (RBAC)</li>
          <li>Backup regular e armazenamento em ambiente segregado</li>
          <li>Treinamento contínuo da equipe em boas práticas de segurança</li>
          <li>
            Contratos de confidencialidade com parceiros que eventualmente
            processem dados em nome da ASHIR GROUP
          </li>
        </ul>
        <p className="mt-4">
          Nenhum sistema é 100% imune a incidentes. Caso identifiquemos uma
          violação de dados que possa gerar risco relevante aos direitos e
          liberdades dos titulares, comunicaremos a ANPD e os titulares
          afetados nos prazos previstos em lei.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    icon: Shield,
    title: "5. Cookies e Tecnologias Similares",
    body: (
      <>
        <p>
          Utilizamos cookies para melhorar a experiência de navegação e
          analisar tráfego. As categorias e preferências podem ser gerenciadas
          no banner de cookies exibido na primeira visita e reabertas a
          qualquer momento via configurações do navegador.
        </p>
      </>
    ),
  },
  {
    id: "contato",
    icon: Mail,
    title: "6. Encarregado (DPO) & Contato",
    body: (
      <>
        <p>
          Dúvidas, solicitações e comunicações sobre esta Política de
          Privacidade devem ser encaminhadas a:
        </p>
        <div className="mt-4 border border-[#D4AF37]/25 p-5 bg-[#0B101A]">
          <div className="text-white font-medium">ASHIR GROUP</div>
          <div className="text-[#A3ABB8] text-sm mt-1 font-light">
            {SITE.address.street}
            <br />
            {SITE.address.building}
            <br />
            {SITE.address.city} — {SITE.address.cep}
          </div>
          <div className="text-[#D4AF37] text-sm mt-3">
            E-mail:{" "}
            <a
              href={`mailto:${SITE.email}`}
              className="underline underline-offset-4 hover:text-[#F0CC59]"
            >
              {SITE.email}
            </a>
          </div>
          <div className="text-[#A3ABB8] text-xs mt-2 tracking-[0.08em]">
            CNPJ nº {SITE.cnpj}
          </div>
        </div>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title =
      "Política de Privacidade | ASHIR GROUP — LGPD e proteção de dados";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App min-h-screen" data-testid="privacy-page">
      <Header />

      <main className="pt-32 md:pt-40 pb-20 bg-[#05070D]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Back link */}
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[#A3ABB8] hover:text-[#D4AF37] transition-colors mb-10"
          >
            <ArrowLeft size={13} strokeWidth={1.5} />
            Voltar para a página inicial
          </Link>

          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <div className="gold-line" />
            <span className="overline">Documento Institucional</span>
          </div>
          <h1 className="font-serif-ag text-white text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-[-0.015em]">
            Política de{" "}
            <span className="text-[#D4AF37] italic">Privacidade</span>
          </h1>
          <p className="mt-6 text-[#A3ABB8] text-base md:text-lg font-light leading-relaxed max-w-2xl">
            A ASHIR GROUP respeita a privacidade dos titulares e trata os dados
            pessoais em conformidade com a Lei Geral de Proteção de Dados —
            LGPD (Lei nº 13.709/2018).
          </p>
          <div className="mt-4 text-xs tracking-[0.14em] uppercase text-[#A3ABB8]">
            Última atualização: <span className="text-white">{UPDATED}</span>
          </div>

          <hr className="my-12 border-white/5" />

          {/* Sections */}
          <div className="space-y-14">
            {sections.map((s) => (
              <section
                key={s.id}
                id={s.id}
                data-testid={`privacy-section-${s.id}`}
                className="scroll-mt-32"
              >
                <div className="flex items-center gap-3 mb-5">
                  <s.icon size={20} strokeWidth={1.3} className="text-[#D4AF37]" />
                  <h2 className="font-serif-ag text-white text-2xl md:text-3xl font-light">
                    {s.title}
                  </h2>
                </div>
                <div className="text-[#D6DBE2] text-base font-light leading-relaxed space-y-1">
                  {s.body}
                </div>
              </section>
            ))}
          </div>

          <hr className="my-16 border-white/5" />
          <p className="text-[#A3ABB8] text-xs tracking-[0.12em] uppercase">
            © {new Date().getFullYear()} ASHIR GROUP · CNPJ {SITE.cnpj} · Todos
            os direitos reservados.
          </p>
        </div>
      </main>

      <TrustBar />
      <Footer />
      <FloatingWhatsApp />
      <CookieConsent />
    </div>
  );
}
